// Obra › Clima › ¿Cuándo hago…?: recomendador de días para hormigonar, impermeabilizar, pintar, mover suelos
// e izar con grúa. Criterios explícitos y explicados en castellano simple; los umbrales los ajusta el cliente.
import React, { useMemo } from 'react';
import { Check, X, Info } from 'lucide-react';
import R from './reglasClima';
import { Seccion, Anillo, Icono3D, mm, grados, diaCorto } from './ui';

const NIVEL = {
  ideal: { txt: 'Ideal', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', hex: '#10b981' },
  posible: { txt: 'Posible, con cuidados', cls: 'bg-amber-50 text-amber-800 border-amber-200', hex: '#f59e0b' },
  no: { txt: 'No recomendado', cls: 'bg-rose-50 text-rose-700 border-rose-200', hex: '#e11d48' },
};

const criteriosTexto = (t, U) => ({
  hormigonado: [`Sin lluvia ${U.ventanaAntes} h antes y ${U.ventanaDespues} h después (curado)`, `Temperatura entre ${U.hormigonTmin}° y ${U.hormigonTmax}°`, 'Sin heladas en los 3 días siguientes', `Ráfagas por debajo de ${U.hormigonRafaga} km/h`, 'Humedad mayor al 40 %'],
  impermeabilizacion: [`Superficie seca: sin lluvia ${U.ventanaAntes} h antes`, 'Sin lluvia en el día ni en las 24 h siguientes', 'Temperatura de 5° o más', 'Humedad por debajo del 85 %', 'Ráfagas por debajo de 30 km/h (soplete)'],
  pintura: ['Sin lluvia en el día ni 12 h después', 'Temperatura entre 10° y 30°', 'Humedad por debajo del 85 %', 'Sin rocío sobre la pared', 'Ráfagas por debajo de 25 km/h'],
  suelos: ['Menos de 10 mm de lluvia en las 48 h anteriores', 'Menos de 2 mm en el horario de trabajo', 'Sin suelo congelado'],
  grua: [`Ráfagas por debajo de ${U.gruaRafaga} km/h en todo el horario`, 'Viento sostenido menor a 30 km/h', 'Sin tormenta eléctrica ni lluvia fuerte', 'Visibilidad de 1 km o más'],
}[t]);

function TarjetaDia({ r, ix, rank }) {
  const N = NIVEL[r.nivel];
  const d = ix.diasMap.get(r.fecha);
  const kind = d ? R.tipoTiempo(d.code, d.mm, false) : 'nublado';
  return (
    <div className={`rounded-2xl border-2 bg-white p-4 flex flex-col gap-3 ${rank === 0 ? 'border-emerald-300 shadow-md' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3">
        <Anillo valor={r.puntaje} size={64} grosor={7} />
        <div className="min-w-0 flex-1">
          {rank === 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Mejor día</p>}
          <p className="text-base font-bold text-gray-900 leading-tight">{R.mayuscula(R.fechaLarga(r.fecha))}</p>
          <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${N.cls}`}>{N.txt}</span>
        </div>
        {d && <div className="text-center shrink-0"><Icono3D kind={kind} size={40} /><p className="text-[11px] text-gray-600">{grados(d.tmax)} · {mm(d.mm)} mm</p></div>}
      </div>
      <p className="text-sm text-gray-700 leading-snug">{r.explicacion}</p>
      <ul className="space-y-1">
        {r.criterios.map((c) => (
          <li key={c.texto} className={`flex items-start gap-1.5 text-xs ${c.aviso ? 'text-gray-500' : c.ok ? 'text-gray-700' : 'text-rose-700 font-semibold'}`}>
            {c.aviso ? <Info className="w-3.5 h-3.5 shrink-0 mt-px" /> : c.ok ? <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-px" /> : <X className="w-3.5 h-3.5 shrink-0 mt-px" />}{c.texto}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Tareas({ ix, U, hoy, tarea, onTarea, onUmbrales }) {
  const fechas = useMemo(() => ix.dias.filter((d) => d.fecha >= hoy).map((d) => d.fecha), [ix, hoy]);
  const todas = useMemo(() => fechas.map((f) => R.evaluarTarea(tarea, ix, f, U)).filter(Boolean), [fechas, tarea, ix, U]);
  const mejores = useMemo(() => R.mejoresDias(tarea, ix, fechas, U, 3), [tarea, ix, fechas, U]);
  // resumen de las 5 tareas para las pastillas
  const resumen = useMemo(() => Object.keys(R.TAREAS).map((k) => ({ k, top: R.mejoresDias(k, ix, fechas, U, 1)[0] })), [ix, fechas, U]);
  const T = R.TAREAS[tarea];
  return (
    <div className="space-y-4 clima-in">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {resumen.map(({ k, top }) => (
          <button key={k} type="button" onClick={() => onTarea(k)} aria-pressed={tarea === k}
            className={`rounded-2xl border-2 p-3 text-left transition-all ${tarea === k ? 'border-blue-600 bg-blue-50/60 shadow-sm' : 'border-gray-200 bg-white hover:border-blue-300'}`}>
            <span className="text-2xl" aria-hidden="true">{R.TAREAS[k].emoji}</span>
            <p className="text-sm font-semibold text-gray-900 mt-1 leading-tight">{R.TAREAS[k].nombre}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{top ? <>Mejor: <b className="text-gray-800 capitalize">{diaCorto(top.fecha)}</b> ({top.puntaje})</> : 'Sin días buenos'}</p>
          </button>
        ))}
      </div>

      <Seccion
        titulo={<>{T.emoji} ¿Cuándo conviene {T.nombre.toLowerCase()}?</>}
        sub={`${T.bajada}. Los mejores días hábiles de las próximas 2 semanas, con puntaje de 0 a 100.`}
        right={<button type="button" onClick={onUmbrales} className="text-xs font-semibold text-blue-700 hover:underline">Ajustar criterios</button>}
      >
        {mejores.length ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {mejores.map((r, i) => <TarjetaDia key={r.fecha} r={r} ix={ix} rank={i} />)}
          </div>
        ) : <p className="text-sm text-gray-600">No hay pronóstico horario suficiente para recomendar días.</p>}

        <div className="mt-5">
          <p className="text-xs font-semibold text-gray-500 mb-2">Día por día</p>
          <div className="space-y-1">
            {todas.map((r) => (
              <div key={r.fecha} className={`flex items-center gap-2 sm:gap-3 ${!r.laborable ? 'opacity-50' : ''}`} title={r.explicacion}>
                <span className="w-20 shrink-0 text-xs font-semibold text-gray-700 capitalize">{diaCorto(r.fecha)}</span>
                <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full flex items-center justify-end pr-2 text-[10px] font-bold text-white" style={{ width: `${Math.max(8, r.puntaje)}%`, background: NIVEL[r.nivel].hex }}>{r.puntaje}</div>
                </div>
                <span className="hidden sm:block w-[45%] text-[11px] text-gray-600 truncate">{r.laborable ? r.explicacion : 'No se trabaja'}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
          <p className="text-xs font-semibold text-gray-700 mb-1.5">Qué miramos para {T.nombre.toLowerCase()}</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {criteriosTexto(tarea, U).map((c) => <li key={c} className="text-xs text-gray-600 flex gap-1.5"><Check className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-px" />{c}</li>)}
          </ul>
          <p className="text-[11px] text-gray-500 mt-2">Puntaje: 80 o más es ideal, de 60 a 79 se puede con cuidados, menos de 60 no se recomienda. Es una guía basada en el pronóstico: la decisión final es del director de obra.</p>
        </div>
      </Seccion>
    </div>
  );
}
